import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, Plus, Pencil, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  adminListHikvisionGtins,
  adminUpsertHikvisionGtin,
  adminDeleteHikvisionGtin,
  adminListHikvisionGtinAudit,
} from "@/lib/admin-hikvision-gtins.functions";

export const Route = createFileRoute("/_authenticated/admin/hikvision-gtins")({
  head: () => ({
    meta: [
      { title: "Admin — Hikvision EAN-lookup" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: HikvisionGtinsPage,
});

type Entry = {
  mpn: string;
  gtin: string;
  note: string | null;
  created_at: string;
  updated_at: string;
};

function HikvisionGtinsPage() {
  const list = useServerFn(adminListHikvisionGtins);
  const upsert = useServerFn(adminUpsertHikvisionGtin);
  const remove = useServerFn(adminDeleteHikvisionGtin);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "hikvision-gtins"],
    queryFn: () => list(),
  });

  const [editing, setEditing] = useState<Entry | null>(null);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const upsertMut = useMutation({
    mutationFn: (input: { mpn: string; gtin: string; note?: string; originalMpn?: string }) =>
      upsert({ data: input }),
    onSuccess: (res) => {
      setFlash(`Opgeslagen: ${res.mpn} → ${res.gtin}`);
      setFormError(null);
      setEditing(null);
      setCreating(false);
      qc.invalidateQueries({ queryKey: ["admin", "hikvision-gtins"] });
      setTimeout(() => setFlash(null), 4000);
    },
    onError: (e: any) => setFormError(e?.message ?? "Opslaan mislukt"),
  });

  const deleteMut = useMutation({
    mutationFn: (mpn: string) => remove({ data: { mpn } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "hikvision-gtins"] });
      setFlash("Verwijderd");
      setTimeout(() => setFlash(null), 3000);
    },
    onError: (e: any) => setFormError(e?.message ?? "Verwijderen mislukt"),
  });

  const entries: Entry[] = (data?.entries as Entry[]) ?? [];

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Hikvision EAN-lookup</h1>
          <p className="text-sm text-muted-foreground">
            Beheer EAN's per MPN. Wijzigingen worden gevalideerd op formaat, checksum en duplicaten.
          </p>
        </div>
        <button
          onClick={() => {
            setCreating(true);
            setEditing(null);
            setFormError(null);
          }}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> Nieuwe EAN
        </button>
      </div>

      {flash && (
        <div className="mb-4 flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4" /> {flash}
        </div>
      )}
      {formError && (
        <div className="mb-4 flex items-center gap-2 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="h-4 w-4" /> {formError}
        </div>
      )}

      {(creating || editing) && (
        <EntryForm
          initial={editing ?? undefined}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
            setFormError(null);
          }}
          onSubmit={(v) =>
            upsertMut.mutate({
              mpn: v.mpn,
              gtin: v.gtin,
              note: v.note,
              originalMpn: editing?.mpn,
            })
          }
          submitting={upsertMut.isPending}
          existing={entries}
        />
      )}

      <div className="rounded-md border border-border bg-white">
        {isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden…
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-red-700">{(error as Error).message}</div>
        ) : entries.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">Nog geen EAN's beheerd.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">MPN</th>
                <th className="px-3 py-2 font-semibold">EAN</th>
                <th className="px-3 py-2 font-semibold">Notitie</th>
                <th className="px-3 py-2 font-semibold">Bijgewerkt</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.mpn} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 font-mono">{e.mpn}</td>
                  <td className="px-3 py-2 font-mono">{e.gtin}</td>
                  <td className="px-3 py-2 text-muted-foreground">{e.note ?? ""}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {new Date(e.updated_at).toLocaleString("nl-NL")}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => {
                        setEditing(e);
                        setCreating(false);
                        setFormError(null);
                      }}
                      className="mr-2 inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                    >
                      <Pencil className="h-3 w-3" /> Bewerk
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`EAN ${e.gtin} voor ${e.mpn} verwijderen?`)) {
                          deleteMut.mutate(e.mpn);
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3" /> Verwijder
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AuditLogPanel />
    </div>
  );
}

function AuditLogPanel() {
  const listAudit = useServerFn(adminListHikvisionGtinAudit);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "hikvision-gtins", "audit"],
    queryFn: () => listAudit({ data: { limit: 50 } }),
  });
  const entries = (data?.entries as any[]) ?? [];

  function diffSummary(action: string, oldV: any, newV: any) {
    if (action === "create") {
      return `nieuw: EAN ${newV?.gtin ?? "?"}`;
    }
    if (action === "delete") {
      return `verwijderd: EAN ${oldV?.gtin ?? "?"}`;
    }
    const changes: string[] = [];
    for (const key of ["mpn", "gtin", "note"]) {
      const a = oldV?.[key] ?? null;
      const b = newV?.[key] ?? null;
      if (JSON.stringify(a) !== JSON.stringify(b)) {
        changes.push(`${key}: "${a ?? ""}" → "${b ?? ""}"`);
      }
    }
    return changes.length ? changes.join(" · ") : "geen wijzigingen";
  }

  return (
    <div className="mt-8">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Audit log</h2>
        <button
          onClick={() => refetch()}
          className="rounded border border-border bg-white px-2 py-1 text-xs hover:bg-muted"
        >
          {isFetching ? "Laden…" : "Vernieuwen"}
        </button>
      </div>
      <div className="rounded-md border border-border bg-white">
        {isLoading ? (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden…
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-red-700">{(error as Error).message}</div>
        ) : entries.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">Nog geen wijzigingen gelogd.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Tijdstip</th>
                <th className="px-3 py-2 text-left">Actie</th>
                <th className="px-3 py-2 text-left">MPN</th>
                <th className="px-3 py-2 text-left">Wijziging</th>
                <th className="px-3 py-2 text-left">Door</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-border align-top">
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                    {new Date(e.created_at).toLocaleString("nl-NL")}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
                        e.action === "create"
                          ? "bg-emerald-100 text-emerald-800"
                          : e.action === "delete"
                            ? "bg-red-100 text-red-800"
                            : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {e.action}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{e.mpn}</td>
                  <td className="px-3 py-2 text-xs">
                    {diffSummary(e.action, e.old_values, e.new_values)}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">
                    {e.actor_id ? e.actor_id.slice(0, 8) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function EntryForm({
  initial,
  onCancel,
  onSubmit,
  submitting,
  existing,
}: {
  initial?: Entry;
  onCancel: () => void;
  onSubmit: (v: { mpn: string; gtin: string; note?: string }) => void;
  submitting: boolean;
  existing: Entry[];
}) {
  const [mpn, setMpn] = useState(initial?.mpn ?? "");
  const [gtin, setGtin] = useState(initial?.gtin ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  const [error, setError] = useState<string | null>(null);

  const rawDigits = gtin.replace(/\D+/g, "");
  const normalized =
    rawDigits.length === 14 && rawDigits.startsWith("0")
      ? rawDigits.slice(1)
      : rawDigits;
  const normalizedMpn = mpn.trim().toUpperCase();
  function validateEan(v: string): string | null {
    if (!v) return "EAN is verplicht (13 cijfers).";
    if (v.length !== 13)
      return `EAN moet exact 13 cijfers zijn (nu ${v.length}).`;
    // Mod-10 checksum
    const digits = v.split("").map(Number);
    const check = digits.pop()!;
    const sum = digits.reduce(
      (acc, d, i) => acc + d * (i % 2 === 0 ? 1 : 3),
      0,
    );
    const expected = (10 - (sum % 10)) % 10;
    if (expected !== check)
      return `Ongeldige EAN-checksum (verwacht ${expected}, gekregen ${check}).`;
    return null;
  }
  const formatError = gtin.trim() ? validateEan(normalized) : null;
  const duplicate =
    !formatError && normalized.length === 13
      ? existing.find(
          (e) =>
            e.gtin.replace(/\D+/g, "").replace(/^0(?=\d{13}$)/, "") ===
              normalized && e.mpn.toUpperCase() !== (initial?.mpn ?? "").toUpperCase(),
        )
      : null;
  const dupError = duplicate
    ? `EAN ${normalized} is al gekoppeld aan MPN ${duplicate.mpn}.`
    : null;
  const mpnConflict =
    normalizedMpn &&
    normalizedMpn !== (initial?.mpn ?? "").toUpperCase() &&
    existing.some((e) => e.mpn.toUpperCase() === normalizedMpn)
      ? `MPN ${normalizedMpn} bestaat al – bewerk die entry in plaats van een nieuwe aan te maken.`
      : null;
  const eanError = formatError || dupError;
  const blocked = !!eanError || !!mpnConflict || !normalized;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        const err = validateEan(normalized);
        if (err) {
          setError(err);
          return;
        }
        if (dupError) {
          setError(dupError);
          return;
        }
        if (mpnConflict) {
          setError(mpnConflict);
          return;
        }
        onSubmit({ mpn: mpn.trim(), gtin: normalized, note: note.trim() || undefined });
      }}
      className="mb-4 rounded-md border border-border bg-muted/30 p-4"
    >
      <h2 className="mb-3 text-sm font-semibold">
        {initial ? `Bewerk ${initial.mpn}` : "Nieuwe EAN toevoegen"}
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">MPN</span>
          <input
            required
            value={mpn}
            onChange={(e) => setMpn(e.target.value)}
            placeholder="DS-2CD1143G0-I"
            aria-invalid={!!mpnConflict}
            className={`rounded border bg-white px-2 py-1.5 font-mono text-sm ${
              mpnConflict ? "border-red-500" : "border-border"
            }`}
          />
          {mpnConflict && (
            <span className="text-xs text-red-600">{mpnConflict}</span>
          )}
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">EAN (13 cijfers)</span>
          <input
            required
            inputMode="numeric"
            pattern="\d{13}"
            maxLength={14}
            value={gtin}
            onChange={(e) => {
              setGtin(e.target.value);
              setError(null);
            }}
            placeholder="6941264092544"
            aria-invalid={!!eanError}
            className={`rounded border bg-white px-2 py-1.5 font-mono text-sm ${
              eanError ? "border-red-500" : "border-border"
            }`}
          />
          {eanError ? (
            <span className="text-xs text-red-600">{eanError}</span>
          ) : normalized.length === 13 ? (
            <span className="text-xs text-emerald-600">Geldige EAN-13 ✓</span>
          ) : (
            <span className="text-xs text-muted-foreground">
              Alleen 13-cijferige EAN's worden geaccepteerd.
            </span>
          )}
        </label>

        <label className="flex flex-col gap-1 text-sm sm:col-span-2">
          <span className="font-medium">Notitie (optioneel)</span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="bijv. bron / batch"
            className="rounded border border-border bg-white px-2 py-1.5 text-sm"
          />
        </label>
      </div>
      {error && (
        <div
          role="alert"
          className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={submitting || blocked}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
          Opslaan
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border bg-white px-3 py-1.5 text-sm hover:bg-muted"
        >
          Annuleren
        </button>
      </div>
    </form>
  );
}
